import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  RootStackRouteNames,
  StackRouteName,
  UnifiedStackParam,
} from '../../types/nav';
import {Folder, FolderType} from '../../types';
import React from 'react';

export interface AppDialog {
  appDialogVisible: boolean;
  appDialogBody: (() => React.JSX.Element) | string | undefined;
  appDialogIcon: string | undefined;
  appDialogTitle: string | undefined;
  appDialogControls: React.Component | undefined;
  appDialogOnSelectRight: (() => Promise<void>) | undefined;
  appDialogOnSelectLeftt: (() => Promise<void>) | undefined;
  appDialogLoading: boolean;
}

export interface UIState extends AppDialog {
  isTabBarHidden: boolean;
  isKeyboardOpen: boolean;
  keyboardHeight: number;
  currentPage: StackRouteName;
  parentName: RootStackRouteNames;
  params: UnifiedStackParam;
  isReady: boolean;
  isSignedIn: boolean;
  isBackEnabled: boolean;
  isEditing: boolean;
  toastText: string | undefined;
  toastIcon: string;
  editingMode:
    | 'party'
    | 'folder'
    | 'media'
    | 'search'
    | 'database'
    | undefined;
  folderType: FolderType;
  folderItemMenuOpen: string | undefined;
  isEditingProfile: boolean;
}

const initialState: UIState = {
  isTabBarHidden: false,
  isKeyboardOpen: false,
  keyboardHeight: 0,
  currentPage: 'FilesTab',
  isReady: false,
  isSignedIn: false,
  params: undefined,
  isBackEnabled: false,
  folderType: FolderType.media,
  isEditing: false,
  editingMode: undefined,
  parentName: 'FilesTab',
  folderItemMenuOpen: undefined,
  appDialogVisible: false,
  appDialogBody: undefined,
  appDialogIcon: undefined,
  appDialogTitle: undefined,
  appDialogControls: undefined,
  appDialogOnSelectRight: undefined,
  appDialogOnSelectLeftt: undefined,
  appDialogLoading: false,
  toastText: undefined,
  toastIcon: 'check-circle',
  isEditingProfile: false,
};

const PagesWithoutTabBar = [
  'Chat',
  'EditorProject',
  'Auction',
  'Camera',
];

const PagesWithBackEnabled = [
  'Devices',
  'Databases',
  'Drive',
  'Folder',
  'Chat',
  'EncoderProject',
  'EditorProject',
  'Auction',
  'Premium',
  'Product',
  'WebEditor',
  'ShopConfig',
  'Orders',
  'Order',
  'Shop',
  'Camera',
];

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setCurrentPage(
      state,
      {
        payload: {currentPage, params, parentName},
      }: PayloadAction<{
        currentPage: StackRouteName;
        params: UnifiedStackParam;
        parentName: RootStackRouteNames;
      }>,
    ) {
      console.info({currentPage, parentName});
      state.currentPage = currentPage;
      state.params = params;
      state.parentName = parentName;

      if (PagesWithoutTabBar.includes(currentPage)) {
        state.isTabBarHidden = true;
      } else {
        state.isTabBarHidden = false;
      }

      if (PagesWithBackEnabled.includes(currentPage)) {
        state.isBackEnabled = true;
      } else {
        state.isBackEnabled = false;
      }

      let folderType = FolderType.media;

      if (currentPage === 'FilesTab') folderType = FolderType.media;
      if (currentPage === 'ContactsTab')
        folderType = FolderType.contact;
      if (currentPage === 'ContactsTab')
        folderType = FolderType.contact;
      if (currentPage === 'Contacts') folderType = FolderType.contact;
      if (currentPage === 'Chat') folderType = FolderType.contact;
      if (currentPage === 'SettingsTab')
        folderType = FolderType.settings;
      if (currentPage === 'EncoderTab')
        folderType = FolderType.encoder;
      if (currentPage === 'EncoderProject')
        folderType = FolderType.encoder;
      if (currentPage === 'EditorTab') folderType = FolderType.editor;
      if (currentPage === 'EditorProject')
        folderType = FolderType.editor;
      if (currentPage === 'Devices') folderType = FolderType.settings;
      if (currentPage === 'Databases')
        folderType = FolderType.settings;
      if (currentPage === 'Drive') folderType = FolderType.settings;
      if (currentPage === 'Settings')
        folderType = FolderType.settings;

      if (currentPage === 'Categories') folderType = FolderType.shop;

      if (currentPage === 'Product') folderType = FolderType.shop;

      // if (currentPage === 'Folder') {
      if (parentName === 'EditorTab') folderType = FolderType.editor;
      if (parentName === 'EncoderTab')
        folderType = FolderType.encoder;
      if (parentName === 'ContactsTab')
        folderType = FolderType.contact;
      if (parentName === 'SettingsTab')
        folderType = FolderType.settings;

      if (parentName === 'ShopTab') folderType = FolderType.shop;

      if (parentName === 'FilesTab') folderType = FolderType.media;
      // }

      state.folderType = folderType;
    },
    setFolderMenuItemId(
      state,
      {
        payload: {itemId},
      }: PayloadAction<{itemId: string | undefined}>,
    ) {
      state.folderItemMenuOpen = itemId;
    },
    setAppDialogParams(
      state,
      {
        payload: {
          appDialogVisible,
          appDialogBody,
          appDialogIcon,
          appDialogControls,
          appDialogOnSelectLeftt,
          appDialogOnSelectRight,
          appDialogTitle,
        },
      }: PayloadAction<AppDialog>,
    ) {
      state.appDialogVisible = appDialogVisible;
      state.appDialogBody = appDialogBody;
      state.appDialogIcon = appDialogIcon;
      state.appDialogControls = appDialogControls as any;
      state.appDialogOnSelectLeftt = appDialogOnSelectLeftt;
      state.appDialogOnSelectRight = appDialogOnSelectRight;
      state.appDialogTitle = appDialogTitle;
    },
    setToastText(state, {payload}: {payload: string | undefined}) {
      state.toastText = payload;
    },
    setToastIcon(state, {payload}: {payload: string}) {
      state.toastIcon = payload;
    },
    setDialogLoading(state, {payload}: {payload: boolean}) {
      state.appDialogLoading = payload;
    },
    setKeyboardHeight(
      state,
      {
        payload: {keyboardHeight},
      }: PayloadAction<{keyboardHeight: number}>,
    ) {
      state.keyboardHeight = keyboardHeight;
      if (keyboardHeight === 0) {
        state.isKeyboardOpen = false;
      } else {
        state.isKeyboardOpen = true;
      }
    },
    setEditingMode(
      state,
      {payload}: PayloadAction<UIState['editingMode']>,
    ) {
      state.editingMode = payload;
      if (payload === undefined || payload === 'media')
        state.isEditing = false;
      else state.isEditing = true;
    },
    setIsReady(state, {payload}: PayloadAction<boolean>) {
      state.isReady = payload;
    },
    setIsSigned(state, {payload}: PayloadAction<boolean>) {
      state.isSignedIn = payload;
    },
    setIsEditingProfile(state, {payload}: PayloadAction<boolean>) {
      state.isEditingProfile = payload;
    },
  },
});
export const {
  setCurrentPage,
  setKeyboardHeight,
  setIsReady,
  setIsSigned,
  setEditingMode,
  setFolderMenuItemId,
  setAppDialogParams,
  setToastText,
  setToastIcon,
  setIsEditingProfile,
  setDialogLoading,
} = uiSlice.actions;
export default uiSlice.reducer;
